import { useCallback, useRef, useState } from 'react';
import { z } from 'zod';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _omit from 'lodash/omit';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _mergeWith from 'lodash/mergeWith';
import _isFunction from 'lodash/isFunction';
import _isPlainObject from 'lodash/isPlainObject';

import { formUtils } from 'utils/methods';

/**
 * @template T
 * @param {Object} args
 * @param {T} args.dataSchema - Zod schema for validating form data.
 * @param {z.infer<T>} args.defaultData - Initial data for the form.
 * @returns {{
 *  data: z.infer<T>,
 *  initialData: z.infer<T>,
 *  errors: Partial<z.infer<T>>,
 *  isDirty: boolean,
 *  onReset: () => void,
 *  onParse: () => z.infer<T>,
 *  onChange: (updates: Partial<z.infer<T>>, config?: import('utils/types').FormValidationConfig) => void,
 *  onValidate: (dataset?: Partial<z.infer<T>>, config?: import('utils/types').FormValidationConfig) => boolean,
 *  onSetInitialData: (initialFormData: z.infer<T>) => void
 *  onSetSchema: (updater: (currentSchema: T) => z.ZodObject) => void
 * }}
 */
export default function useForm({
  defaultData = {},
  dataSchema = z.object({})
} = {}) {
  const [data, setData] = useState(defaultData);
  const [schema, setSchema] = useState(dataSchema);
  const [errors, setErrors] = useState({});
  const initialDataRef = useRef(defaultData);

  /**
   * @param {Partial<T>} updates - Dataset to validate.
   * @param {import('utils/types').FormValidationConfig} config - Additional configuration for validation.
   * @param {T} formData - Complete form-data.
   * @returns {boolean} - Returns true if validation passes, false otherwise.
   * @description Validates the updates against the schema and updates errors state.
   */
  const onValidate = useCallback(
    (updates = {}, config = {}, formData) => {
      const runValidation = _get(config, 'runValidation', true);
      const validationPaths = _get(config, 'validationPaths', []);

      if (!runValidation) return true;

      const paths = _isEmpty(validationPaths)
        ? formUtils.getErrorPaths(updates)
        : validationPaths;
      const issuesMap = formUtils.validate(formData || updates, schema);

      setErrors(prevErrors => {
        let newErrors = { ...prevErrors };
        paths.forEach(path => {
          if (path in issuesMap) {
            newErrors = _set(newErrors, path, issuesMap[path]);
          } else {
            newErrors = _omit(newErrors, path);
          }
        });

        return formUtils.getNonEmptyDeepCopy(newErrors);
      });

      return _isEmpty(issuesMap);
    },
    [schema]
  );

  /** @description Resets the form data to its initial state. */
  const onReset = useCallback(() => setData(initialDataRef.current), []);

  /** @returns {z.infer<T>} - Parsed form data according to the current schema. */
  const onParse = useCallback(() => {
    return formUtils.getParsedData(data, schema);
  }, [data, schema]);

  /**
   * @param {Partial<T>} [updates={}] - Updates to apply to the form data.
   * @param {import('utils/types').FormValidationConfig} [config={}] - Additional configuration for validation.
   * @description Merges updates into the current form data and optionally validates the updated data.
   */
  const onChange = useCallback(
    (updates = {}, config = {}) => {
      if (!_isPlainObject(updates) || _isEmpty(updates)) {
        return;
      }

      setData(prevData => {
        const newData = _mergeWith({}, prevData, updates, (_, v) => v);
        onValidate(updates, config, newData);
        return newData;
      });
    },
    [onValidate]
  );

  /**
   * @param {(currentSchema: T) => z.ZodObject} updater - Function to update the current schema.
   * @description Updates the form schema using the provided updater function.
   */
  const onSetSchema = useCallback((updater = () => {}) => {
    if (!_isFunction(updater)) return;
    setSchema(currSchema => updater(currSchema));
  }, []);

  /**
   * @param {T} initialFormData - Initial data to set for the form.
   * @description Sets the initial data reference and updates the form data and errors state.
   * */
  const onSetInitialData = useCallback(initialFormData => {
    initialDataRef.current = initialFormData;
    setData(initialFormData);
    setErrors({});
  }, []);

  return {
    data,
    initialData: initialDataRef.current,
    errors,
    isDirty: !_isEqual(data, initialDataRef.current),
    onReset,
    onParse,
    onChange,
    onValidate,
    onSetSchema,
    onSetInitialData
  };
}
